import type { TestimonialsBlockProps } from '../types';
import { theme, sectionColors } from '../theme';

function stars(rating?: number): string | null {
  if (typeof rating !== 'number' || !Number.isFinite(rating)) return null;
  const n = Math.max(0, Math.min(5, Math.round(rating)));
  if (n === 0) return null;
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

/**
 * Pure testimonials grid — client reviews as quote cards. Static content; a
 * responsive grid (server-safe, no carousel JS). Renders nothing when empty.
 */
export function TestimonialsBlock(props: TestimonialsBlockProps) {
  const { heading, testimonials = [] } = props;
  const colors = sectionColors(props, { bg: theme.surface, text: theme.text });
  if (!Array.isArray(testimonials) || testimonials.length === 0) return null;

  const grid =
    testimonials.length === 1
      ? 'sm:grid-cols-1'
      : testimonials.length === 2
        ? 'sm:grid-cols-2'
        : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-6xl">
        {heading ? (
          <h2
            className="mb-8 text-center text-2xl font-semibold"
            style={{ fontFamily: theme.headingFont }}
          >
            {heading}
          </h2>
        ) : null}
        <div className={`grid grid-cols-1 gap-6 ${grid}`}>
          {testimonials.map((t, i) => {
            const rated = stars(t.rating);
            return (
              <figure
                key={i}
                className="flex flex-col rounded-lg border border-black/10 bg-white p-6"
              >
                {rated ? (
                  <div className="mb-3 text-sm tracking-wide" style={{ color: theme.primary }}>
                    {rated}
                  </div>
                ) : null}
                <blockquote className="flex-1 text-sm leading-relaxed">“{t.quote}”</blockquote>
                <figcaption className="mt-4 text-sm font-semibold">
                  {t.author}
                  {t.location ? (
                    <span className="font-normal opacity-60"> · {t.location}</span>
                  ) : null}
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
